// src/component/AdminGroupActivity2026BulkJoin.js
//
// 2026グループ 一斉参加登録（/admin/group-activity-2026-bulk-join）
//
// テキストエリアに改行区切りで貼られたユーザー uid を一括で
// 「グループ活動2026に参加する」状態にする管理者専用ツール。
//
// 書き込みパターンは UserAdminPage の個別代理登録と同じ：
//   users/{uid}.groupActivity2026 = {
//     response: "join",
//     respondedAt: serverTimestamp(),
//     setByAdmin: true,  // 事務局代理書き込みフラグ
//   }
//
// 既存回答の扱い：
//   - "join" 既登録：スキップして「既に参加済み」として一覧表示
//   - "decline" → "join" に上書き
//   - 未回答 → "join" を新規セット
//   - ドキュメント未発見 → 失敗扱い

import React, { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Button,
  Paper,
  CircularProgress,
  IconButton,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import GroupAddRoundedIcon from "@mui/icons-material/GroupAddRounded";

import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../api/firebase";
import { AppThemeContext } from "./AppShell";

// 入力テキストを uid 配列にパース。空行と前後空白は除去、重複排除。
function parseUids(text) {
  const seen = new Set();
  const out = [];
  (text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((u) => {
      if (!seen.has(u)) {
        seen.add(u);
        out.push(u);
      }
    });
  return out;
}

export default function AdminGroupActivity2026BulkJoin() {
  const navigate = useNavigate();
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive || "#5A35FF";
  const surface = theme.surfaceBg || "#FFFFFF";
  const appBg = theme.appBg || "#F5F5F9";
  const textBase = theme.railText || "rgba(40,40,60,0.9)";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";

  const [text, setText] = useState("");
  const [checking, setChecking] = useState(false);
  const [writing, setWriting] = useState(false);
  const [error, setError] = useState("");

  // 確認ダイアログのデータ。null なら閉じている。
  //   { toUpdate: string[], alreadyJoined: string[], notFound: string[] }
  const [preview, setPreview] = useState(null);

  // 実行結果。null なら未実行 or リセット済み。
  //   { success: number, failed: number, alreadyJoined: number, notFound: number }
  const [result, setResult] = useState(null);

  const uids = useMemo(() => parseUids(text), [text]);

  const onCheck = async () => {
    setError("");
    setResult(null);
    if (uids.length === 0) {
      setError("uid を 1 件以上入力してください。");
      return;
    }
    setChecking(true);
    try {
      // 全 uid を並列で取得して、現状の groupActivity2026.response を見る。
      const snaps = await Promise.all(
        uids.map((uid) => getDoc(doc(db, "users", uid)))
      );
      const alreadyJoined = [];
      const toUpdate = [];
      const notFound = [];
      snaps.forEach((s, i) => {
        const uid = uids[i];
        if (!s.exists()) {
          notFound.push(uid);
          return;
        }
        const data = s.data() || {};
        const current = data.groupActivity2026?.response;
        if (current === "join") {
          alreadyJoined.push(uid);
        } else {
          toUpdate.push(uid);
        }
      });
      setPreview({ toUpdate, alreadyJoined, notFound });
    } catch (e) {
      console.error("preview fetch error:", e);
      setError(`uid の確認に失敗しました：${e.message || e}`);
    } finally {
      setChecking(false);
    }
  };

  const onConfirm = async () => {
    if (!preview) return;
    setWriting(true);
    setError("");
    try {
      // updateDoc を並列発火。失敗したものは個別カウント。
      const results = await Promise.allSettled(
        preview.toUpdate.map((uid) =>
          updateDoc(doc(db, "users", uid), {
            groupActivity2026: {
              response: "join",
              respondedAt: serverTimestamp(),
              setByAdmin: true,
            },
          })
        )
      );
      let success = 0;
      let failed = 0;
      results.forEach((r) => {
        if (r.status === "fulfilled") success += 1;
        else failed += 1;
      });
      setResult({
        success,
        failed,
        alreadyJoined: preview.alreadyJoined.length,
        notFound: preview.notFound.length,
      });
      setPreview(null);
    } catch (e) {
      console.error("bulk write error:", e);
      setError(`一斉登録中にエラーが発生しました：${e.message || e}`);
    } finally {
      setWriting(false);
    }
  };

  const onCancel = () => {
    if (writing) return;
    setPreview(null);
  };

  const onReset = () => {
    setText("");
    setResult(null);
    setError("");
  };

  return (
    <Box sx={{ minHeight: "100%", background: appBg, pb: 6 }}>
      <Box
        sx={{
          maxWidth: 900,
          mx: "auto",
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 3 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowBackRoundedIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 800, color: textBase }}>
            2026グループ 一斉参加登録
          </Typography>
        </Stack>

        <Paper
          sx={{
            p: { xs: 2, sm: 2.5 },
            mb: 2,
            border: `1px solid ${border}`,
            borderRadius: 2,
            bgcolor: surface,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: textBase, opacity: 0.85, lineHeight: 1.8, mb: 1.5 }}
          >
            下のテキストボックスにユーザー uid を改行区切りで貼り付け、
            「一斉グループ参加登録」を押すと、各ユーザーの 2026 グループ活動を
            「参加する」状態に書き換えます（事務局代理書き込み）。
            <br />
            既に「参加する」のユーザーはスキップし、確認ダイアログで一覧表示します。
            「参加しない」「未回答」のユーザーは「参加する」に上書きします。
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {error}
            </Alert>
          )}

          {result && (
            <Alert severity="success" sx={{ mb: 1.5 }}>
              一斉登録が完了しました — 成功 {result.success} 件 / 失敗 {result.failed} 件
              （スキップ：既に参加 {result.alreadyJoined} 件 / 未発見 {result.notFound} 件）
            </Alert>
          )}

          <TextField
            label="ユーザー uid（1 行 1 件）"
            multiline
            minRows={10}
            maxRows={20}
            fullWidth
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={checking || writing}
            placeholder={"uid1\nuid2\nuid3\n..."}
            sx={{ mb: 1.5 }}
          />

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
          >
            <Typography
              variant="caption"
              sx={{ color: textBase, opacity: 0.7 }}
            >
              入力済み：{uids.length} 件
            </Typography>
            <Box sx={{ flex: 1 }} />
            {result && (
              <Button
                variant="text"
                onClick={onReset}
                disabled={checking || writing}
                sx={{ textTransform: "none" }}
              >
                クリア
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={
                checking ? (
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                ) : (
                  <GroupAddRoundedIcon />
                )
              }
              onClick={onCheck}
              disabled={checking || writing || uids.length === 0}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: accent,
                "&:hover": { bgcolor: accent, opacity: 0.92 },
              }}
            >
              一斉グループ参加登録
            </Button>
          </Stack>
        </Paper>
      </Box>

      <Dialog
        open={!!preview}
        onClose={onCancel}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          一斉参加登録の確認
        </DialogTitle>
        <DialogContent dividers>
          {preview && (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={`入力 ${uids.length} 件`}
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
                <Chip
                  label={`登録対象 ${preview.toUpdate.length} 件`}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 700 }}
                />
                <Chip
                  label={`既に参加 ${preview.alreadyJoined.length} 件`}
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
                {preview.notFound.length > 0 && (
                  <Chip
                    label={`未発見 ${preview.notFound.length} 件`}
                    size="small"
                    color="warning"
                    sx={{ fontWeight: 700 }}
                  />
                )}
              </Stack>

              {preview.alreadyJoined.length > 0 && (
                <>
                  <Divider />
                  <Typography
                    variant="caption"
                    sx={{ color: textBase, opacity: 0.7, fontWeight: 700 }}
                  >
                    以下のユーザーはすでに「参加する」のため、スキップします：
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      maxHeight: 180,
                      overflow: "auto",
                      bgcolor: "rgba(0,0,0,0.02)",
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{
                        m: 0,
                        fontFamily: "monospace",
                        fontSize: 12,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                        color: textBase,
                      }}
                    >
                      {preview.alreadyJoined.join("\n")}
                    </Typography>
                  </Paper>
                </>
              )}

              {preview.notFound.length > 0 && (
                <>
                  <Divider />
                  <Typography
                    variant="caption"
                    sx={{ color: "#b08800", fontWeight: 700 }}
                  >
                    以下の uid に該当するユーザーが見つかりません：
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      maxHeight: 120,
                      overflow: "auto",
                      bgcolor: "#fff7e0",
                      borderColor: "#e0c060",
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{
                        m: 0,
                        fontFamily: "monospace",
                        fontSize: 12,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                        color: "#8a6500",
                      }}
                    >
                      {preview.notFound.join("\n")}
                    </Typography>
                  </Paper>
                </>
              )}

              <Divider />
              <Typography sx={{ color: textBase, fontWeight: 700 }}>
                {preview.toUpdate.length} 件のユーザーを「参加する」に登録します。
                よろしいですか？
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onCancel}
            disabled={writing}
            sx={{ textTransform: "none" }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={writing || !preview || preview.toUpdate.length === 0}
            startIcon={
              writing ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : null
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
              bgcolor: accent,
              "&:hover": { bgcolor: accent, opacity: 0.92 },
            }}
          >
            実行する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
